import moment from 'moment';
import { BOOKING_STAGES, COURSE_TYPE } from '../../../constants';
import { ActionCellRenderer } from '../_components/ActionCellRenderer';
import SessionQuestions from '../_components/SessionQuestionsCellRenderer';
import { TooltipComponent } from '../_components/TooltipComponent';
import { styledDefaultColDef } from '../../../extras/cellRenderers';

export const debounceMs = 2000;
export const columnDefs = (themes, subTopics) => [
  {
    headerName: 'Course',
    field: 'courseType',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: ['Data Science', 'Web Development'],
      debounceMs: debounceMs,
    },
    valueFormatter: (param) =>
      ({
        [COURSE_TYPE.DATA_SCIENCE]: 'Data Science',
        [COURSE_TYPE.WEB3]: 'Web Development',
      }[param.value]),
  },

  {
    headerName: 'Theme',
    field: 'themeName',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: themes?.map((theme) => theme.name),
      debounceMs: debounceMs,
    },
  },
  {
    headerName: 'Sub Topic',
    field: 'subTopicName',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: subTopics?.map((subTopic) => subTopic.name),
      debounceMs: debounceMs,
    },
    valueGetter: (params) => {
      return params.data.subTopicName;
    },
  },
  {
    headerName: 'Student',
    field: 'Student',
    sortable: false,

    filter: false,
    valueGetter: (params) => {
      return [
        params.data.Student?.firstName,
        params.data.Student?.lastName,
      ].join(' ');
    },
  },
  {
    headerName: 'Coach',
    field: 'Coach',
    sortable: false,
    filter: false,
    valueGetter: (params) =>
      [params.data.Coach?.firstName, params.data.Coach?.lastName].join(' '),
  },
  {
    headerName: 'Exclusive Coach',
    field: 'ExclusiveCoach',
    filter: false,
    sortable: false,

    valueGetter: (params) =>
      [
        params.data.ExclusiveCoach?.firstName,
        params.data.ExclusiveCoach?.lastName,
      ].join(' '),
  },
  {
    headerName: 'Status',
    field: 'status',
    sortable: false,
    width: 120,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: ['completed', 'defaulted', 'active', 'cancelled'],
      debounceMs: debounceMs,
    },
    cellStyle: (params) => {
      switch (params.value) {
        case 'active':
          return { color: '#FDA623' };
        case 'completed':
          return { color: '#76CE65' };
        case 'cancelled':
        case 'defaulted':
          return { color: '#CC0000' };
      }
    },
  },
  {
    headerName: 'Stage',
    field: 'stage',
    sortable: false,
    agSetColumnFilter: 'agSetColumnFilter',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: Object.entries(BOOKING_STAGES).map(([key, value]) => value.text),
      debounceMs: debounceMs,
    },
    valueGetter: (params) => {
      if (params.data.problemReportedByStudent)
        return 'Problem reported by student';
      else if (params.data.problemReportedByCoach)
        return 'Problem reported by coach';
      else
        return Object.values(BOOKING_STAGES).find(
          (stage) => stage.value === params.data.stage
        )?.text;
    },
    width: 160,
  },
  {
    headerName: 'Booked At',
    field: 'createdAt',
    filter: 'agDateColumnFilter',
    filterParams: {
      filterOptions: ['inRange', 'equals'],
      suppressAndOrCondition: true,
    },
    tooltipField: 'createdAt',
    valueFormatter: (param) =>
      moment(param.value).format('DD MMM YYYY, hh:mm A'),
    suppressAndOrCondition: true,
  },
  {
    headerName: 'Scheduled At',
    field: 'startTime',
    filter: 'agDateColumnFilter',
    filterParams: {
      filterOptions: ['inRange', 'equals'],
      suppressAndOrCondition: true,
    },
    tooltipField: 'startTime',
    valueFormatter: (param) =>
      param.value && moment(param.value).format('DD MMM YYYY, hh:mm A'),
  },
  {
    headerName: 'Conducted At',
    field: 'conductedAt',
    filter: 'agDateColumnFilter',
    filterParams: {
      filterOptions: ['inRange', 'equals'],
      suppressAndOrCondition: true,
    },
    tooltipField: 'conductedAt',
    valueFormatter: (param) =>
      param.value && moment(param.value).format('DD MMM YYYY, hh:mm A'),
  },
  {
    headerName: 'Preferred Slots',
    field: 'preferences',
    filter: false,
    sortable: false,
    width: 300,
    wrapText: true,
    cellRenderer: 'agGroupCellRenderer',
    tooltipField: 'Time Slots',
    valueFormatter: (param) => {
      const first = param?.value?.at(0);
      const last = param?.value?.at(-1);
      if (first && last) {
        return `${
          moment(first?.date).format('DD MMM YY') +
          ' ' +
          moment(first?.timeSlots?.at(0).startTime, 'HH:mm:ss').format('LT')
        } ,  ${
          moment(last?.date).format('DD MMM YY') +
          ' ' +
          moment(last?.timeSlots?.at(-1)?.endTime, 'HH:mm:ss').format('LT')
        } `;
      }
      return `N/A`;
    },
  },
  {
    headerName: 'Questions',
    field: 'noOfQuestions',
    filter: 'agNumberColumnFilter',
    filterParams: {
      filterOptions: ['equals'],
      suppressAndOrCondition: true,
    },
    cellRenderer: SessionQuestions,
  },
  {
    headerName: 'StudentQuestionRating',
    field: 'avgQuestionRatingByStudent',
    filter: false,
    valueFormatter: (param) => param.value || '-',
  },
  {
    headerName: 'CoachQuestionRating',
    field: 'avgQuestionRatingByCoach',
    filter: false,
    valueFormatter: (param) => param.value || '-',
  },
  {
    headerName: 'StudentSessionRating',
    field: 'avgSessionRatingByStudent',
    filter: false,
    valueFormatter: (param) => param.value || '-',
  },
  {
    headerName: 'CoachSessionRating',
    field: 'avgSessionRatingByCoach',
    filter: false,
    valueFormatter: (param) => param.value || '-',
  },
  {
    field: 'expiryDate',
  },
  {
    field: 'id',
    sortable: false,
    filter: false,
  },
  {
    headerName: 'Actions',
    pinned: 'right',
    sortable: false,
    suppressMenu: true,
    width: 120,
    cellRenderer: ActionCellRenderer,
  },
];

export const defaultColDef = {
  ...styledDefaultColDef,
  filter: true,
  tooltipComponent: TooltipComponent,
};

export const detailCellRendererParams = () => {
  return {
    detailGridOptions: {
      columnDefs: [
        {
          field: 'date',
          valueFormatter: (param) =>
            param.value && moment(param.value).format('DD MMM YYYY'),
        },
        {
          field: 'timeSlots',
          valueFormatter: (param) => {
            let slots = '';
            for (const [index, val] of param.value.entries()) {
              slots =
                slots +
                `${moment(val.startTime, 'HH:mm:ss').format('LT')} ${
                  index < param.value.length - 1 ? ',' : ''
                } `;
            }

            return slots || 'N/A';
          },
        },
      ],
      defaultColDef: {
        ...styledDefaultColDef,
        suppressMenu: true,
        flex: 1,
        filter: false,
      },
      onGridReady: (params) => {
        params.api.setDomLayout('autoHeight');
      },
    },
    getDetailRowData: (params) => {
      const data = params.data.preferences || [];
      params.successCallback(data);
    },
  };
};

export const ACTIONS = {
  CANCEL_BOOKING: 'Cancel Session',
  ASSIGN_COACH: 'Assign Coach',
  REPLACE_COACH: 'Replace Coach',
  REVIEW_SESSION: 'Review Session',
  SESSION_DETAILS: 'Session Details',
  RESCHEDULE_SESSION: 'Reschedule',
  UPDATE_DATETIME: 'Update Date & Time',
  REACTIVATE_BOOKING: 'Reactivate Session',
};

export const STAGE_ACTIONS = {
  [BOOKING_STAGES.CREATED.value]: [
    ACTIONS.CANCEL_BOOKING,
    ACTIONS.ASSIGN_COACH,
  ],
  [BOOKING_STAGES.TIME_SLOTS_ADDED.value]: [
    ACTIONS.CANCEL_BOOKING,
    ACTIONS.ASSIGN_COACH,
    ACTIONS.RESCHEDULE_SESSION,
  ],
  [BOOKING_STAGES.COACH_ASSIGNED.value]: [
    ACTIONS.CANCEL_BOOKING,
    ACTIONS.REPLACE_COACH,
    ACTIONS.RESCHEDULE_SESSION,
    ACTIONS.UPDATE_DATETIME,
  ],
  [BOOKING_STAGES.SESSION_CONDUCTED.value]: [
    ACTIONS.CANCEL_BOOKING,
    ACTIONS.REVIEW_SESSION,
  ],
  [BOOKING_STAGES.FEEDBACK_SUBMITTED.value]: [ACTIONS.REVIEW_SESSION],
  [BOOKING_STAGES.ADMIN_ON_HOLD.value]: [ACTIONS.REVIEW_SESSION],
  [BOOKING_STAGES.ADMIN_APPROVED.value]: [ACTIONS.SESSION_DETAILS],
  [BOOKING_STAGES.CANCELLED.value]: [ACTIONS.REACTIVATE_BOOKING],
};
